/**
 * Singleton token store.
 *
 * Bridges Auth0 React context (useAuth0) to the RTK Query prepareHeaders
 * callback, which runs outside React component lifecycle.
 *
 * TokenUpdater (inside Auth0Provider) calls setTokenGetter() to register
 * the getAccessTokenSilently function. prepareHeaders calls getToken() to
 * retrieve a fresh token.
 *
 * getToken() waits up to 5s for the getter to be registered, preventing
 * race conditions on initial page load.
 */

type TokenGetter = () => Promise<string>

let _tokenGetter: TokenGetter | null = null
let _resolveReady: (() => void) | null = null
const _ready = new Promise<void>((resolve) => {
  _resolveReady = resolve
})

export function setTokenGetter(getter: TokenGetter): void {
  _tokenGetter = getter
  if (_resolveReady) {
    _resolveReady()
    _resolveReady = null
  }
}

export async function getToken(): Promise<string | null> {
  if (!_tokenGetter) {
    // Wait up to 5s for TokenUpdater to register the getter
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 5000))
    await Promise.race([_ready, timeout])
  }
  if (!_tokenGetter) return null
  try {
    return await _tokenGetter()
  } catch {
    return null
  }
}
